'use client'

import { motion } from 'motion/react'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { useTimer } from '@/api/timers'
import { toMediaCdnUrl } from '@/utils/imageUrl'
import { isValidHttpUrl } from '@/utils/url'

import { useCountdown } from '../../hooks/useCountdown'
import {
  fadeInUp,
  revealViewport,
  staggerContainer
} from '../../utils/animations'
import S from './SalesBanner.module.scss'

interface SalesBannerProps {
  targetDate?: Date
  refreshIntervalMs?: number
  onCtaClick?: () => void
}

const SalesBanner = ({
  targetDate,
  refreshIntervalMs = 60_000,
  onCtaClick
}: SalesBannerProps) => {
  const t = useTranslations('news.banner')
  const { data: timer, isLoading } = useTimer()

  // No synthetic fallback — if the parent didn't force a date AND the
  // DB doesn't have an active timer, the banner is suppressed below.
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- optional-chain dep prevents Compiler memo; manual useMemo still correct (OCE-527)
  const countdownTarget = useMemo<Date | null>(() => {
    if (targetDate) {
      return targetDate
    }
    if (timer?.deadline) {
      const deadlineDate = new Date(timer.deadline)
      if (!Number.isNaN(deadlineDate.getTime())) {
        return deadlineDate
      }
    }
    return null
  }, [targetDate, timer?.deadline])

  // useCountdown handles a null targetDate (skips the interval entirely);
  // do NOT substitute a placeholder Date here — passing a freshly-created
  // Date on every render churns the effect deps and produced a render
  // storm that blocked navigation clicks on pages hosting the banner
  // (OCE-1287).
  const { days, hours, minutes, isExpired } = useCountdown({
    targetDate: countdownTarget,
    refreshIntervalMs
  })

  if (isLoading || !countdownTarget) {
    return null
  }

  const ctaUrl = isValidHttpUrl(timer?.see_more_link)
    ? timer?.see_more_link
    : undefined
  const backgroundUrl = isValidHttpUrl(timer?.background_image)
    ? toMediaCdnUrl(timer?.background_image)
    : undefined

  const handleCtaClick = () => {
    if (ctaUrl) {
      window.open(ctaUrl, '_blank', 'noopener,noreferrer')
    }
    onCtaClick?.()
  }

  const title = timer?.title || t('title')
  const subtitle = timer?.body || t('subtitle')

  const formatTime = (value: number) => value.toString().padStart(2, '0')

  const isTimerZero = days === 0 && hours === 0 && minutes === 0

  if (isLoading) {
    return null
  }

  if (isExpired || isTimerZero) {
    return null
  }

  return (
    <motion.section
      className={S.salesBanner}
      aria-label={title}
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={revealViewport}
      style={
        backgroundUrl
          ? {
              backgroundImage: `url(${backgroundUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }
          : undefined
      }
    >
      <motion.div
        className={S.salesBannerContainer}
        variants={staggerContainer}
      >
        <motion.div className={S.salesBannerContent} variants={fadeInUp}>
          <h3 className={S.salesBannerTitle}>{title}</h3>
          <p className={S.salesBannerSubtitle}>{subtitle}</p>
        </motion.div>
        <motion.div className={S.salesBannerTimer} variants={staggerContainer}>
          <div className={S.timerItem}>
            <motion.span
              key={days}
              className={S.timerValue}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {formatTime(days)}
            </motion.span>
            <span className={S.timerLabel}>{t('timer.days')}</span>
          </div>
          <span className={S.timerSeparator}>:</span>
          <div className={S.timerItem}>
            <motion.span
              key={hours}
              className={S.timerValue}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {formatTime(hours)}
            </motion.span>
            <span className={S.timerLabel}>{t('timer.hours')}</span>
          </div>
          <span className={S.timerSeparator}>:</span>
          <div className={S.timerItem}>
            <motion.span
              key={minutes}
              className={S.timerValue}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {formatTime(minutes)}
            </motion.span>
            <span className={S.timerLabel}>{t('timer.minutes')}</span>
          </div>
        </motion.div>
        <motion.button
          type="button"
          className={S.salesBannerButton}
          onClick={handleCtaClick}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.98 }}
          transition={{ duration: 0.2 }}
        >
          {t('cta')}
        </motion.button>
      </motion.div>
    </motion.section>
  )
}

export default SalesBanner
